import { Component, OnInit, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { LocalizationService } from '../../core/localization/localization.service';
import { WorkflowApiService, WorkflowIntentEvaluationRunDetail, WorkflowIntentEvaluationSample } from './workflow-api.service';

@Component({
  standalone: true,
  selector: 'ain-workflow-intent-simulation-run',
  imports: [
    AinPageComponent,
    AinStateComponent,
    FormsModule,
    CardModule,
    ButtonModule,
    InputTextModule,
    SelectModule
  ],
  template: `
    <ain-page [title]="t('workflow.intents.eval.simulation.title')" [description]="t('workflow.intents.eval.simulation.description')">
      @if (loading()) {
        <ain-state type="loading" [title]="t('common.loading')" />
      } @else if (error()) {
        <ain-state type="error" [title]="error() ?? ''" />
      } @else {
        <p-card>
          <div class="form-grid">
            <div class="field">
              <label>{{ t('workflow.intents.eval.fields.datasetKey') }}</label>
              <p-select
                [options]="datasetOptions()"
                optionLabel="label"
                optionValue="value"
                [(ngModel)]="datasetKey"
                [placeholder]="t('workflow.intents.eval.fields.datasetKey')"></p-select>
            </div>
            <div class="field">
              <label>{{ t('workflow.intents.eval.fields.windowFromUtc') }}</label>
              <input pInputText [(ngModel)]="windowFromUtc" [placeholder]="'2026-01-01T00:00:00Z'" />
            </div>
            <div class="field">
              <label>{{ t('workflow.intents.eval.fields.windowToUtc') }}</label>
              <input pInputText [(ngModel)]="windowToUtc" [placeholder]="'2026-12-31T23:59:59Z'" />
            </div>
          </div>
          <div class="actions">
            <button pButton type="button" [label]="t('workflow.intents.eval.actions.runSimulation')" [loading]="running()" (click)="runSimulation()"></button>
          </div>
          @if (lastRun()) {
            <div class="summary">
              <strong>{{ t('workflow.intents.eval.simulation.lastRun') }}</strong>
              <div>{{ t('workflow.intents.eval.columns.datasetKey') }}: {{ lastRun()!.run.datasetKey }}</div>
              <div>{{ t('workflow.intents.eval.metrics.classificationAccuracy') }}: {{ lastRun()!.metrics?.classificationAccuracyProxy ?? 0 }}</div>
              <div>{{ t('workflow.intents.eval.metrics.slotCompleteness') }}: {{ lastRun()!.metrics?.slotFillCompleteness ?? 0 }}</div>
            </div>
          }
        </p-card>
      }
    </ain-page>
  `,
  styles: [`
    .form-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); gap: 12px; }
    .field { display: grid; gap: 6px; }
    .actions { margin-top: 12px; display: flex; justify-content: flex-end; }
    .summary { margin-top: 12px; display: grid; gap: 4px; }
  `]
})
export class WorkflowIntentSimulationRunComponent implements OnInit {
  private readonly api = inject(WorkflowApiService);
  private readonly localization = inject(LocalizationService);
  private readonly router = inject(Router);

  readonly loading = signal(true);
  readonly running = signal(false);
  readonly error = signal<string | null>(null);
  readonly samples = signal<WorkflowIntentEvaluationSample[]>([]);
  readonly lastRun = signal<WorkflowIntentEvaluationRunDetail | null>(null);

  datasetKey = '';
  windowFromUtc = '';
  windowToUtc = '';

  ngOnInit(): void {
    this.api.listIntentEvaluationSamples(undefined, true).subscribe({
      next: samples => {
        this.samples.set(samples);
        this.datasetKey = samples[0]?.datasetKey ?? '';
        this.loading.set(false);
      },
      error: () => {
        this.loading.set(false);
        this.error.set(this.t('common.loadFailed'));
      }
    });
  }

  datasetOptions(): Array<{ label: string; value: string }> {
    const datasetKeys = Array.from(new Set(this.samples().map(sample => sample.datasetKey)));
    return datasetKeys.map(key => ({ label: key, value: key }));
  }

  runSimulation(): void {
    if (!this.datasetKey) {
      this.error.set(this.t('workflow.intents.eval.validation.datasetRequired'));
      return;
    }

    this.running.set(true);
    this.error.set(null);
    this.api.executeIntentSimulation({
      datasetKey: this.datasetKey,
      windowFromUtc: this.windowFromUtc || undefined,
      windowToUtc: this.windowToUtc || undefined
    }).subscribe({
      next: result => {
        this.running.set(false);
        this.lastRun.set(result);
        void this.router.navigate(['/workflow/intents/evaluation/runs', result.run.id]);
      },
      error: () => {
        this.running.set(false);
        this.error.set(this.t('common.saveFailed'));
      }
    });
  }

  t(key: string): string {
    return this.localization.translate(key);
  }
}
